/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createToolingURL } from '@watson/tooling-url';
import urlJoin from 'proper-url-join';

import { SEARCH_SKILL_TYPE } from '@ibm-watson/shared/modules/channels/util/channelTypes';
import { DISCOVERY_TEMPLATE_ID } from '@ibm-watson/shared/modules/ia/store/skills/helper';

const DISCO_V2_PREMIUM_DEV_PLAN_ID = '9c9086c4-74ed-4855-9c07-6f019352b78c.dev';
const DISCO_V2_PREMIUM_PLAN_ID = '9c9086c4-74ed-4855-9c07-6f019352b78c';
const DISCO_V2_PREMIUM_RELEASE_DATES = {
  'us-south': '2020-05-15T00:00:00.000Z',
  'us-east': '2020-05-15T00:00:00.000Z',
  'eu-de': '2020-07-07T00:00:00.000Z',
  'jp-tok': '2020-08-11T00:00:00.000Z',
  'eu-gb': '2021-02-01T00:00:00.000Z'
};

export const page = {
  CONNECTION_PAGE: 'connection',
  CONFIGURATION_PAGE: 'configuration'
};

/**
 * Identifies if the Discovery instance is V2 or V1
 * @param {*} instance the service instance
 * @returns {Boolean} True if the service instance is v2
 **/
// eslint-disable-next-line complexity
export const isInstanceV2 = instance => {
  if (instance) {
    const {
      plan = {},
      region,
      metadata = {},
      entity = {},
      extensions = {}
    } = instance;

    if (extensions.discovery && extensions.discovery.version === '2.0') {
      return true;
    }

    // For dev & staging disco v2 premium instance - testing only
    if (
      plan.guid === DISCO_V2_PREMIUM_DEV_PLAN_ID ||
      entity.name === 'Discovery-staging-premium-v2'
    ) {
      return true;
    }

    // If plan id is discoV2 premium plans & instance creation date is greater than the date mentioned per region in
    // https://github.ibm.com/watson-deploy-configs/discovery/search?q=RESOURCE_CONTROLLER_RESOURCE_CREATED_AFTER&unscoped_q=RESOURCE_CONTROLLER_RESOURCE_CREATED_AFTER
    // instance is v2 premium.
    if (
      plan.guid === DISCO_V2_PREMIUM_PLAN_ID &&
      region in DISCO_V2_PREMIUM_RELEASE_DATES
    ) {
      if (
        new Date(metadata.created_at) >
        new Date(DISCO_V2_PREMIUM_RELEASE_DATES[region])
      ) {
        return true;
      }
    }
    return false;
  }
  return false;
};

export const filterFields = fields => {
  let filteredFields = fields.filter(({ type }) => type === 'string');
  // For Disco V2, sometimes we do get duplicate fields, This will take remove any duplicate onces.
  filteredFields = Array.from(
    new Set(filteredFields.map(({ field }) => field))
  ).map(field => ({ field }));
  return filteredFields;
};

export const getDefaultFieldValueForV2Projects = fields => {
  const defaultTitle = fields.some(({ field }) => field === 'title')
    ? 'title'
    : fields.some(({ field }) => field === 'extracted_metadata.title')
    ? 'extracted_metadata.title'
    : '';
  const defaultBody = fields.some(({ field }) => field === 'text')
    ? 'text'
    : fields.some(({ field }) => field === 'body')
    ? 'body'
    : '';
  const defaultUrl = fields.some(({ field }) => field === 'metadata.source.url')
    ? 'metadata.source.url'
    : '';

  return {
    defaultTitle,
    defaultBody,
    defaultUrl
  };
};

export const generateSkillData = params => {
  return {
    skill_id: params.id,
    name: params.name,
    description: params.description,
    search_settings: {
      discovery: {
        collection_id: params.discoCollectionId || params.collectionId,
        environment_id: params.environmentId,
        project_id: params.projectId,
        instance_id: params.instanceId,
        hightlight: true,
        passages: false,
        results_count: 10
      },
      messages: {
        error: params.errorMessage,
        no_result: params.noResultMessage,
        success: params.message
      },
      schema_mapping: {
        body: params.schemaBody,
        title: params.schemaTitle,
        url: params.schemaUrl
      }
    },
    cfApi: params.cfApi
  };
};

// eslint-disable-next-line complexity
export function buildSearchSkillQueryParamsString(page, skill, instance) {
  if (!page || !skill || !instance) {
    return '';
  }
  const { id, name, description, config } = skill;
  return (
    `?template_id=${SEARCH_SKILL_TYPE}` +
    (skill?.config?.waEnvironmentId
      ? `&environment_id=${skill.config.waEnvironmentId}`
      : '') +
    '&page=' +
    encodeURIComponent(page) +
    '&id=' +
    encodeURIComponent(id || '') +
    '&name=' +
    encodeURIComponent(name) +
    (description ? `&description=${encodeURIComponent(description)}` : '') +
    '&instanceId=' +
    encodeURIComponent(config?.instance_id) +
    (config?.collection_id
      ? `&collectionId=${encodeURIComponent(config?.collection_id)}`
      : '') +
    (config?.environment_id
      ? `&environmentId=${encodeURIComponent(config?.environment_id)}`
      : '') +
    (config?.project_id
      ? `&projectId=${encodeURIComponent(config?.project_id)}`
      : '') +
    (config?.message ? `&message=${encodeURIComponent(config?.message)}` : '') +
    '&noResultMessage=' +
    encodeURIComponent(config?.no_result_message || '') +
    '&errorMessage=' +
    encodeURIComponent(config?.error_message || '') +
    '&schemaTitle=' +
    encodeURIComponent(config?.schema_mapping_title || '') +
    '&schemaBody=' +
    encodeURIComponent(config?.schema_mapping_body || '') +
    '&schemaUrl=' +
    encodeURIComponent(config?.schema_mapping_url || '') +
    '&discoveryRegion=' +
    encodeURIComponent(instance?.region) +
    (instance?.cfApi ? `&cfApi=${encodeURIComponent(instance?.cfApi)}` : '') +
    '&instanceName=' +
    encodeURIComponent(instance?.name || instance?.entity?.name || '')
  );
}

// generate the link to the connected discovery instance
// eslint-disable-next-line complexity
export function generateDiscoveryLink(
  connectedInstance,
  originalLocation,
  currentPage,
  skill,
  shouldRedirect
) {
  let discoveryLink;
  const config = skill.config;

  if (connectedInstance) {
    const page = currentPage === 'connection' ? currentPage : 'configuration';
    const result = buildSearchSkillQueryParamsString(
      page,
      skill,
      connectedInstance
    );

    // To fix this bug https://github.ibm.com/Watson-Discovery/score-squad-tracker/issues/569
    // adding encodeURIComponent around crn for v1 tooling redirect only
    const redirect_uri =
      originalLocation.origin +
      (config?.collection_id
        ? encodeURIComponent(originalLocation.pathname)
        : originalLocation.pathname) +
      result;

    // Below block is for CPD
    if (connectedInstance.tooling_url) {
      let discoveryLink = urlJoin(
        `https://${window.location.host}`,
        connectedInstance.tooling_url,
        'projects'
      );
      if (config.project_id) {
        discoveryLink = urlJoin(
          discoveryLink,
          encodeURIComponent(config.project_id),
          'workspace'
        );
      } else {
        discoveryLink = urlJoin(discoveryLink, 'new/setup/type');
      }
      if (shouldRedirect) {
        discoveryLink += `?redirect_uri=${encodeURIComponent(
          redirect_uri
        )}&redirect_key=wa`;
      }
      return discoveryLink;
    }

    let consoleUrl;
    if (
      (connectedInstance?.id?.includes('crn') &&
        connectedInstance?.id?.includes('staging')) ||
      (connectedInstance?.cfApi && connectedInstance?.cfApi?.includes('stage1'))
    ) {
      consoleUrl = 'stage1';
    }

    const serviceInstance = {
      region: connectedInstance.region,
      serviceGuid: connectedInstance.id,
      serviceLabel: 'discovery',
      consoleUrl: consoleUrl
    };

    discoveryLink = createToolingURL({ serviceInstance })?.url;

    if (config?.environment_id && config?.collection_id) {
      discoveryLink =
        discoveryLink +
        `/environments/${encodeURIComponent(config?.environment_id)}` +
        `/collections/${encodeURIComponent(config?.collection_id)}` +
        '/manage/overview';
    }

    if (config?.project_id) {
      discoveryLink = new URL(discoveryLink).origin;
      if (config?.project_id === 'new') {
        discoveryLink =
          discoveryLink +
          `/v2/instances/${encodeURIComponent(
            connectedInstance.id
          )}/projects/new/setup/type`;
      } else {
        discoveryLink =
          discoveryLink +
          `/v2/instances/${encodeURIComponent(connectedInstance.id)}/projects/${
            config?.project_id
          }/workspace`;
      }
    }

    // `ftue_flag` should be set programatically once discovery auto-provisioning is implemented
    if (shouldRedirect) {
      discoveryLink += `?redirect_uri=${encodeURIComponent(
        redirect_uri
      )}&redirect_key=wa`;
    }

    discoveryLink = discoveryLink.replace(
      'discovery-tooling-s',
      'discovery-tooling-d'
    );
  }

  return discoveryLink;
}

/*
 * Transforms Search skill data format from V2 to V1.
 *
 * @param {{
 *   assistant_references: array;
 *   description: string;
 *   id: string;
 *   language: string;
 *   created: string;
 *   updated: string;
 *   name: string;
 *   search_settings: {
 *     discovery: {
 *       collection_id: string;
 *       environment_id: string;
 *       highlight: boolean;
 *       instance_id: string;
 *       passages: boolean;
 *       results_count: number;
 *       url: string;
 *     };
 *     messages: {
 *       error: string;
 *       no_result: string;
 *       success: string;
 *     };
 *     schema_mapping: {
 *       body: string;
 *       title: string;
 *       url: string;
 *     };
 *   };
 *   skill_id: string;
 *   id: string;
 *   type: string;
 * }} v2Data
 * @returns {{
 *   agent_definitions: array;
 *   description: string;
 *   id: string;
 *   timestamp_created: string;
 *   timestamp_modified: string;
 *   name: string;
 *   config: {
 *     collection_id: string;
 *     discovery_url: string;
 *     environment_id: string;
 *     error_message: string;
 *     highlight: boolean;
 *     instance_id: string;
 *     message: string;
 *     no_result_message: string;
 *     pagination_enabled: boolean;
 *     results_per_page: number;
 *     schema_mapping_body: string;
 *     schema_mapping_title: string;
 *     schema_mapping_url: string;
 *   };
 *   skill_reference: string;
 *   version: string;
 *   next_skill_version: string;
 *   tenant_id: string;
 *   worker_template_id: string;
 * }} corresponding data converted into V1 format
 */
export function mapSearchSkillDataV2ToV1(v2Data) {
  let v1Data = {};
  if (v2Data) {
    v1Data = {
      agent_definitions: v2Data.assistant_references,
      description: v2Data.description ? v2Data.description : '',
      id: v2Data.id ? v2Data.id : v2Data.skill_id,
      timestamp_created: v2Data.created,
      timestamp_modified: v2Data.updated,
      name: v2Data.name,
      skill_reference: 'search skill',
      version: 'v1',
      next_skill_version: '1',
      tenant_id: undefined,
      worker_template_id: DISCOVERY_TEMPLATE_ID,
      config: {}
    };
    const v2SearchSettings = v2Data.search_settings;
    if (
      v2SearchSettings &&
      v2SearchSettings.discovery &&
      v2SearchSettings.messages &&
      v2SearchSettings.schema_mapping
    ) {
      v1Data.config = {
        collection_id: v2SearchSettings.discovery.collection_id,
        discovery_url: v2Data.search_settings.discovery.url,
        environment_id: v2Data.search_settings.discovery.environment_id,
        error_message: v2Data.search_settings.messages.error,
        highlight: v2Data.search_settings.discovery.highlight,
        instance_id: v2Data.search_settings.discovery.instance_id,
        message: v2Data.search_settings.messages.success,
        no_result_message: v2Data.search_settings.messages.no_result,
        pagination_enabled: false,
        results_per_page: v2Data.search_settings.discovery.results_count,
        schema_mapping_body: v2Data.search_settings.schema_mapping.body,
        schema_mapping_title: v2Data.search_settings.schema_mapping.title,
        schema_mapping_url: v2Data.search_settings.schema_mapping.url
      };
    }
  }
  return v1Data;
}

/*
 * Transforms Search skill data format from V1 to V2.
 *
 * @param {{
 *   agent_definitions: array;
 *   description: string;
 *   id: string;
 *   timestamp_created: string;
 *   timestamp_modified: string;
 *   name: string;
 *   config: {
 *     collection_id: string;
 *     discovery_url: string;
 *     environment_id: string;
 *     error_message: string;
 *     highlight: boolean;
 *     instance_id: string;
 *     message: string;
 *     no_result_message: string;
 *     pagination_enabled: boolean;
 *     results_per_page: number;
 *     schema_mapping_body: string;
 *     schema_mapping_title: string;
 *     schema_mapping_url: string;
 *   };
 *   skill_reference: string;
 *   version: string;
 *   next_skill_version: string;
 *   tenant_id: string;
 *   worker_template_id: string;
 * }} v1Data
 * @returns {{
 *   assistant_references: array;
 *   description: string;
 *   language: string;
 *   created: string;
 *   updated: string;
 *   name: string;
 *   search_settings: {
 *     discovery: {
 *       collection_id: string;
 *       environment_id: string;
 *       highlight: boolean;
 *       instance_id: string;
 *       passages: boolean;
 *       results_count: number;
 *       url: string;
 *     };
 *     messages: {
 *       error: string;
 *       no_result: string;
 *       success: string;
 *     };
 *     schema_mapping: {
 *       body: string;
 *       title: string;
 *       url: string;
 *     };
 *   };
 *   skill_id: string;
 *   id: string;
 *   type: string;
 * }} corresponding data converted into V2 format
 */
export function mapSearchSkillDataV1ToV2(v1Data) {
  let v2Data = {};
  if (v1Data) {
    v2Data = {
      assistant_references: v1Data.agent_definitions,
      description: v1Data.description ? v1Data.description : '',
      language: undefined,
      created: v1Data.timestamp_created,
      updated: v1Data.timestamp_modified,
      name: v1Data.name,
      skill_id: v1Data.id,
      id: v1Data.id,
      type: 'search'
    };
    if (v1Data.config) {
      v2Data.search_settings = {
        discovery: {
          collection_id: v1Data.config.collection_id,
          environment_id: v1Data.config.environment_id,
          highlight: v1Data.config.highlight,
          instance_id: v1Data.config.instance_id,
          passages: false,
          results_count: v1Data.config.results_per_page,
          url: v1Data.config.discovery_url
        },
        messages: {
          error: v1Data.config.error_message,
          no_result: v1Data.config.no_result_message,
          success: v1Data.config.message
        },
        schema_mapping: {
          body: v1Data.config.schema_mapping_body,
          title: v1Data.config.schema_mapping_title,
          url: v1Data.config.schema_mapping_url
        }
      };
    }
  }
  return v2Data;
}
